import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

const MARGEM_VIEWPORT = 8;
const ALTURA_MAX_LISTA = 280;
const ALTURA_BUSCA = 40;
const LARGURA_MIN = 260;

// Calcula onde a lista deve aparecer: abaixo do campo, ou acima quando falta espaço,
// sempre dentro da viewport (a lista é renderizada em portal para não ser cortada
// por containers com overflow, como tabelas roláveis dentro de modais).
function calcularPosicao(rect) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const largura = Math.min(Math.max(rect.width, LARGURA_MIN), vw - MARGEM_VIEWPORT * 2);
  const left = Math.min(Math.max(rect.left, MARGEM_VIEWPORT), vw - largura - MARGEM_VIEWPORT);
  const espacoAbaixo = vh - rect.bottom - MARGEM_VIEWPORT - 4;
  const espacoAcima = rect.top - MARGEM_VIEWPORT - 4;
  const abrirAcima = espacoAbaixo < 200 && espacoAcima > espacoAbaixo;
  const espaco = abrirAcima ? espacoAcima : espacoAbaixo;
  const alturaLista = Math.max(Math.min(ALTURA_MAX_LISTA, espaco - ALTURA_BUSCA), 80);
  return abrirAcima
    ? { left, width: largura, bottom: vh - rect.top + 4, alturaLista }
    : { left, width: largura, top: rect.bottom + 4, alturaLista };
}

// Select com campo de busca: clique abre a lista, digitar filtra as opções.
// `options` é um array de { value, label }. Compatível com o estilo dos <select>
// nativos usados nos modais de Gestão Orçamentária (mesma paleta C).
export default function ComboBoxFiltravel({ C, value, onChange, options, placeholder = "Selecione…", emptyLabel = "Nenhuma opção encontrada" }) {
  const [aberto, setAberto] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [posicao, setPosicao] = useState(null);
  const raizRef = useRef(null);
  const listaRef = useRef(null);

  const selecionado = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    if (!aberto) setFiltro("");
  }, [aberto]);

  useEffect(() => {
    function aoClicarFora(e) {
      if (raizRef.current?.contains(e.target) || listaRef.current?.contains(e.target)) return;
      setAberto(false);
    }
    document.addEventListener("mousedown", aoClicarFora);
    return () => document.removeEventListener("mousedown", aoClicarFora);
  }, []);

  useLayoutEffect(() => {
    if (!aberto) return undefined;
    function atualizar() {
      if (raizRef.current) setPosicao(calcularPosicao(raizRef.current.getBoundingClientRect()));
    }
    function aoRolar(e) {
      if (listaRef.current?.contains(e.target)) return;
      atualizar();
    }
    function aoTeclar(e) {
      if (e.key === "Escape") setAberto(false);
    }
    atualizar();
    window.addEventListener("resize", atualizar);
    window.addEventListener("scroll", aoRolar, true);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      window.removeEventListener("resize", atualizar);
      window.removeEventListener("scroll", aoRolar, true);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  const opcoesFiltradas = useMemo(() => {
    const termo = filtro.trim().toLowerCase();
    if (!termo) return options;
    return options.filter((o) => o.label.toLowerCase().includes(termo));
  }, [filtro, options]);

  return (
    <div ref={raizRef} style={{ position: "relative" }}>
      <div
        onClick={() => setAberto((v) => !v)}
        style={{
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
          padding: "9px 12px", fontSize: 13, color: selecionado ? C.text : C.muted,
          fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center",
          justifyContent: "space-between", gap: 8,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selecionado ? selecionado.label : placeholder}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.6 }} />
      </div>

      {aberto && posicao && createPortal(
        <div ref={listaRef} style={{
          position: "fixed", top: posicao.top, bottom: posicao.bottom, left: posicao.left, width: posicao.width,
          zIndex: 1000, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
          boxShadow: "0 12px 32px rgba(0,0,0,0.25)", overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>
          <input
            autoFocus
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Digite para filtrar…"
            style={{
              width: "100%", boxSizing: "border-box", background: C.bg, border: "none",
              borderBottom: `1px solid ${C.border}`, padding: "9px 12px", fontSize: 13,
              color: C.text, fontFamily: "inherit", outline: "none", flexShrink: 0,
            }}
          />
          <div style={{ maxHeight: posicao.alturaLista, overflowY: "auto", overscrollBehavior: "contain" }}>
            {value !== "" && value != null && (
              <div
                onClick={() => { onChange(""); setAberto(false); }}
                style={{ padding: "8px 12px", fontSize: 13, color: C.muted, cursor: "pointer", fontStyle: "italic" }}
              >
                Limpar seleção
              </div>
            )}
            {opcoesFiltradas.length === 0 && (
              <div style={{ padding: "8px 12px", fontSize: 13, color: C.muted }}>{emptyLabel}</div>
            )}
            {opcoesFiltradas.map((o) => (
              <div
                key={o.value}
                onClick={() => { onChange(o.value); setAberto(false); }}
                style={{
                  padding: "8px 12px", fontSize: 13, color: C.text, cursor: "pointer",
                  background: String(o.value) === String(value) ? C.bgActive : "transparent",
                }}
                onMouseEnter={(e) => { if (String(o.value) !== String(value)) e.currentTarget.style.background = C.bgActive; }}
                onMouseLeave={(e) => { if (String(o.value) !== String(value)) e.currentTarget.style.background = "transparent"; }}
              >
                {o.label}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
